import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function InteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Interior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Thousand Oaks', 'Ventura County', 'Los Angeles'],
    description: 'Professional mobile interior car detailing services including deep cleaning, shampooing, steam sanitizing, and leather conditioning.'
  };
  const faqs = [{
    question: 'What does interior detailing include?',
    answer: 'Our interior detail includes deep vacuuming, shampooing of carpets and fabric seats, leather cleaning and conditioning, steam sanitizing of vents and high-touch surfaces, plastic and vinyl restoration, and streak-free interior glass.'
  }, {
    question: 'How long does interior detailing take?',
    answer: 'Interior detailing typically takes 2\u20133 hours depending on the size and condition of your vehicle. Heavily soiled interiors or pet hair removal may take longer.'
  }, {
    question: 'Can you remove stains and odors?',
    answer: 'Yes. Our shampooing and steam extraction lift most deep-set stains, and our sanitizing process neutralizes odors from spills, smoke, and pets.'
  }, {
    question: 'How much does interior detailing cost in Ventura County?',
    answer: 'Interior detailing starts at $180, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you come to my home or office?',
    answer: 'Yes \u2014 we are fully mobile and self-contained, bringing our own water and power to your home or office anywhere in Ventura County.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Interior Detailing',
    item: 'https://www.fulldetailpro.com/services/interior-detailing'
  }];
  return <ServicePageLayout title="Interior Detailing Ventura County" subtitle="Restore your vehicle's cabin to a factory-fresh feel with our comprehensive mobile interior detailing service. We come directly to your home or office." metaDescription="Expert mobile interior detailing in Ventura County \u2014 Camarillo, Oxnard, Ventura & Thousand Oaks. Deep cleaning, stain removal & steam sanitizing. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-742" data-mp="2froc6p15keg">
      <p data-id="element-743" data-mp="3tp8al6zjt2w8">
        Your vehicle's interior is where you spend all your time driving. Over
        time, dirt, dust, spills, pet hair, and odors can accumulate, making
        your daily commute less enjoyable and potentially impacting your health.
        At Mike's Pro Detail, our <strong data-id="element-744" data-mp="14v1hfab97w05">mobile interior car detailing</strong>{' '}
        service is designed to deep clean, sanitize, and restore your cabin to a
        like-new condition. Whether you're in Camarillo CA, Thousand Oaks, or
        anywhere in Ventura County, our fully equipped mobile unit brings
        premium interior detailing directly to your doorstep.
      </p>

      <h2 data-id="element-745" data-mp="17g95ebyv3jak">What Our Interior Detailing Includes</h2>
      <p data-id="element-746" data-mp="17h11dnwdfj1d">
        We don't just wipe down surfaces; we perform a meticulous, multi-step
        deep cleaning process to ensure every crack and crevice is addressed.
        Our comprehensive interior detail includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-747" data-mp="2070www4zfjrk">
        <li className="flex items-start" data-id="element-748" data-mp="1lf8s3e2tdo43">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-749" data-mp="1lefvh5xkkezo" />
          <span data-id="element-750" data-mp="1qdsjhu06fs3u">
            <strong data-id="element-751" data-mp="1qdabk045jpg3">Thorough Vacuuming:</strong> Deep extraction of dirt,
            debris, and pet hair from carpets, seats, floor mats, and the trunk
            area.
          </span>
        </li>
        <li className="flex items-start" data-id="element-752" data-mp="2ixr3ovxobqpp">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-753" data-mp="v7w0y6h9lwt7" />
          <span data-id="element-754" data-mp="q89klo6540g6">
            Cleaning of fabric seats, carpets, and floor mats to lift deep-set
            stains and grime.
          </span>
        </li>
        <li className="flex items-start" data-id="element-755" data-mp="l9ybjfno5tz0">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-756" data-mp="ga3jv9w2l0cw" />
          <span data-id="element-757" data-mp="gbwt8wy2hyol">
            <strong data-id="element-758" data-mp="gcgb6dnpwn2m">Leather Cleaning & Conditioning:</strong> Safe, pH-balanced
            cleaning of leather seats followed by a premium conditioner to
            prevent cracking and fading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-759" data-mp="147rgenhusdz1">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-760" data-mp="146pzw3i2iz9x" />
          <span data-id="element-761" data-mp="z9pvry4s46lg">
            <strong data-id="element-762" data-mp="z86f46f7ntj5">Steam Cleaning & Sanitization:</strong> High-pressure steam
            is used on vents, cup holders, and high-touch areas to kill bacteria
            and melt away sticky residue.
          </span>
        </li>
        <li className="flex items-start" data-id="element-763" data-mp="1rviqdkfx8ex5">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-764" data-mp="206rff4upw13t" />
          <span data-id="element-765" data-mp="208m3gs1ac9c4">
            <strong data-id="element-766" data-mp="247l25uwfb9v4">Plastics & Vinyl Restoration:</strong> Cleaning and UV
            protection applied to the dashboard, door panels, and center console
            to restore a rich, non-greasy finish.
          </span>
        </li>
        <li className="flex items-start" data-id="element-767" data-mp="2e6tuykfjyifw">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-768" data-mp="2e3o6n7eqzkl8" />
          <span data-id="element-769" data-mp="2j4lvu3y6dew8">
            <strong data-id="element-770" data-mp="2j3122gjopzzf">Streak-Free Windows:</strong> Interior glass and mirrors are
            cleaned to a crystal-clear, streak-free shine for optimal
            visibility.
          </span>
        </li>
      </ul>

      <h2 data-id="element-771" data-mp="2rzry4sy8leu0">The Benefits of Professional Interior Detailing</h2>
      <p data-id="element-772" data-mp="2ww0ickrajy81">
        Investing in a professional interior detail goes beyond just aesthetics.
        A clean interior significantly improves the air quality inside your
        vehicle by removing dust mites, allergens, and bacteria trapped in the
        upholstery and ventilation system. Furthermore, regular conditioning of
        leather and UV protection on plastics prevents premature aging,
        cracking, and fading caused by the intense Southern California sun. If
        you're planning to sell your vehicle, a pristine interior is one of the
        most effective ways to maximize its resale value.
      </p>

      <h2 data-id="element-773" data-mp="2m3kjd5vkk2rl">Why Choose Mike's Pro Detail for Interior Detailing?</h2>
      <p data-id="element-774" data-mp="2zvqu2cfydhjn">
        When searching for "Mobile Auto Detailing Camarillo CA" or "Car
        Detailing Camarillo", you want a service that is reliable, meticulous,
        and convenient. Mike's Pro Detail stands out because we use only
        premium, eco-friendly products and professional-grade equipment steam
        cleaners. We understand that your time is valuable, which is why our
        mobile detailing Ventura County service comes entirely to you—fully
        equipped with our own water and power supply. From Camarillo, Thousand
        Oaks, Simi Valley, Woodland Hills, Westlake Village, to Chatsworth, and
        more we deliver 5-star results without you ever having to leave your
        home or office.
      </p>

      <h2 data-id="element-775" data-mp="3r6d77y5srk8w">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-776" data-mp="3cgfqu4e3zyu9">
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-777" data-mp="3hehbuuv3i88d">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-778" data-mp="37i4enn0cdc8v">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-779" data-mp="rox82ju6g6x9">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-780" data-mp="3fp5mjv3xnx27">
          Headlight Restoration &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}